/**
 * Per-route SEO component for the marketing site.
 *
 * Built on `@unhead/react` because react-helmet-async does not officially
 * support React 19 (peer-dep mismatch under strict-peer-dependencies). Behavior
 * is equivalent: head tags are reconciled per route, with hreflang alternates,
 * canonical URL, Open Graph, Twitter card, and optional JSON-LD.
 *
 * Localized title/description are looked up via i18next so the same component
 * works for every supported locale.
 */

import { useHead } from '@unhead/react';
import { useTranslation } from 'react-i18next';
import {
  buildHreflangAlternates,
  buildCanonical,
  LOCALE_METADATA,
  isSupportedLocale,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from '@noxverify/i18n';

const ORIGIN = 'https://www.noxverify.com';

/**
 * Map BCP-47 locales to Facebook Open Graph locale codes. Falls back to
 * `en_US` when an unmapped locale is encountered.
 */
const OG_LOCALE_MAP: Record<string, string> = {
  en: 'en_US',
  ar: 'ar_AR',
  az: 'az_AZ',
  be: 'be_BY',
  de: 'de_DE',
  es: 'es_ES',
  fa: 'fa_IR',
  fr: 'fr_FR',
  hi: 'hi_IN',
  id: 'id_ID',
  it: 'it_IT',
  ja: 'ja_JP',
  ko: 'ko_KR',
  ms: 'ms_MY',
  nl: 'nl_NL',
  pt: 'pt_BR',
  ru: 'ru_RU',
  th: 'th_TH',
  tr: 'tr_TR',
  uk: 'uk_UA',
  vi: 'vi_VN',
  'zh-Hans': 'zh_CN',
};

/**
 * Serialize a JSON-LD block for safe injection into an inline
 * `<script type="application/ld+json">` element.
 *
 * `JSON.stringify` does not escape the literal substring `</script>`, U+2028,
 * or U+2029, all of which can break out of (or otherwise corrupt) an HTML
 * script tag. This is defense-in-depth: even though JSON-LD blocks are not
 * executed as JavaScript, the HTML parser still treats them as script-tag
 * content and a stray `</script>` would terminate the tag early.
 */
function serializeJsonLd(block: object): string {
  return JSON.stringify(block)
    .replace(/<\/(script)/gi, '<\\/$1')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

export interface SEOProps {
  /** i18next key for `<title>`, e.g. `'seo:home.title'`. */
  titleKey: string;
  /** i18next key for `<meta name="description">`. */
  descriptionKey: string;
  /** Logical path WITHOUT locale prefix, e.g. `'/'` or `'/about'`. */
  path: string;
  /** Absolute or root-relative image URL for OG/Twitter cards. */
  image?: string;
  /** One or more JSON-LD structured-data blobs. */
  jsonLd?: object | object[];
  /** Open Graph type — `'website'` (default) or `'article'`. */
  ogType?: 'website' | 'article';
  /** Optional keyword meta — generally low SEO value, but supported. */
  keywordsKey?: string;
}

export function SEO({
  titleKey,
  descriptionKey,
  path,
  image,
  jsonLd,
  ogType = 'website',
  keywordsKey,
}: SEOProps): null {
  const { t, i18n } = useTranslation();
  const rawLang = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
  const locale = (isSupportedLocale(rawLang) ? rawLang : DEFAULT_LOCALE) as SupportedLocale;

  const title = t(titleKey);
  const description = t(descriptionKey);
  const keywords = keywordsKey ? t(keywordsKey) : undefined;
  const canonical = buildCanonical(path, locale, ORIGIN);
  const alternates = buildHreflangAlternates(path, ORIGIN);
  const ogImage = image
    ? image.startsWith('http')
      ? image
      : `${ORIGIN}${image.startsWith('/') ? image : `/${image}`}`
    : `${ORIGIN}/og-image.png`;
  const dir = LOCALE_METADATA[locale].dir;
  const ogLocale = OG_LOCALE_MAP[locale] ?? 'en_US';

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  useHead({
    htmlAttrs: { lang: locale, dir },
    title,
    meta: [
      { name: 'description', content: description },
      ...(keywords ? [{ name: 'keywords', content: keywords }] : []),
      { property: 'og:type', content: ogType },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: ogImage },
      { property: 'og:locale', content: ogLocale },
      { property: 'og:site_name', content: 'NoxVerify' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
    ],
    link: [
      { rel: 'canonical', href: canonical },
      ...alternates.map((a) => ({
        rel: 'alternate',
        hreflang: a.hreflang,
        href: a.href,
      })),
    ],
    script: jsonLdArray.map((block, idx) => ({
      type: 'application/ld+json',
      key: `ld-${idx}`,
      innerHTML: serializeJsonLd(block),
    })),
  });

  return null;
}
